import AlertIcon from '@assets/icons/alert.svg';
import i18n from '@/utils/i18n';
import PlusIcon from '@assets/icons/plus.svg';
import React from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, LIST_MARGIN_HORIZONTAL, TYPOGRAPHY } from '@/styles';
import { PaymentMethodOption } from '@/contexts/payments-context';
import { Text, View } from 'react-native';

interface Props {
  loadingPaymentMethod: boolean | null | undefined;
  navigateToPaymentMethod: () => void | null;
  paymentMethodValidationMessage: string | null;
  selectedPaymentMethod: PaymentMethodOption | null | undefined;
}

export const OrderPaymentMethodSelector: React.FC<Props> = ({
  loadingPaymentMethod,
  navigateToPaymentMethod,
  paymentMethodValidationMessage,
  selectedPaymentMethod,
}) => {
  const [styles, theme] = useStyles();

  return (
    <View style={styles.paymentMethodDetails}>
      <Text style={TYPOGRAPHY.pSemiBold}>{i18n.t('payment_method')}</Text>
      <View style={styles.paymentMethodSelector}>
        {loadingPaymentMethod ? (
          <ActivityIndicator />
        ) : selectedPaymentMethod ? (
          <>
            <Text style={[TYPOGRAPHY.h4]}>
              {selectedPaymentMethod.displayText}
            </Text>
            <Button
              onPress={() => {
                navigateToPaymentMethod();
              }}
              size="small"
              style={styles.changePaymentMethodButton}
              trackingData={{
                name: 'orders.change_payment_method_button',
              }}
              type="tertiary"
            >
              <Text style={[TYPOGRAPHY.small, FONTS.primarySemiBold]}>
                {i18n.t('change')}
              </Text>
            </Button>
          </>
        ) : (
          <>
            <Button
              leftElement={
                <PlusIcon fill={theme.colors.primary} height={20} width={20} />
              }
              onPress={() => {
                navigateToPaymentMethod();
              }}
              size="small"
              style={styles.addPaymentButton}
              trackingData={{
                name: 'orders.add_payment_method_button',
              }}
              type="tertiary"
            >
              <Text style={[TYPOGRAPHY.small, styles.addPaymentText]}>
                {i18n.t('add_payment')}
              </Text>
            </Button>
          </>
        )}
      </View>
      {paymentMethodValidationMessage ? (
        <View style={styles.errorTextContainer}>
          <AlertIcon
            height={TYPOGRAPHY.small.lineHeight}
            stroke={theme.colors.negative}
            width={12}
          />
          <Text style={[TYPOGRAPHY.small, styles.errorText]}>
            {paymentMethodValidationMessage}
          </Text>
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  addPaymentButton: {
    paddingHorizontal: 0,
  },
  addPaymentText: {
    color: theme.colors.inactiveText,
    marginVertical: 8,
  },
  changePaymentMethodButton: {
    paddingHorizontal: 0,
  },
  errorText: {
    color: theme.colors.negative,
    flex: 1,
  },
  errorTextContainer: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  paymentMethodDetails: {
    marginHorizontal: LIST_MARGIN_HORIZONTAL,
    paddingBottom: 24,
    paddingTop: 16,
  },
  paymentMethodSelector: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
}));
